import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  diagnosticRoomsData,
  roomSchedulesData,
  weekdayLabels,
  getKitById,
  getPharmacyById
} from "@/data/platformData";
import {
  CalendarRange, DoorOpen, Plus, Trash2, Save, Clock, AlertOctagon, ArrowLeft
} from "lucide-react";

const PHARMACY_ID = "ph1";

export const ManagerPlanningPage = () => {
  const navigate = useNavigate();
  const pharmacy = getPharmacyById(PHARMACY_ID);
  const rooms = diagnosticRoomsData.filter(r => r.pharmacyId === PHARMACY_ID);

  const initialSchedules = useMemo(() => {
    const map = {};
    rooms.forEach(r => {
      map[r.id] = Array.from({ length: 7 }, (_, w) => {
        const found = roomSchedulesData.find(s => s.roomId === r.id && s.weekday === w);
        return found ? { ...found, slots: [...found.slots] } : { roomId: r.id, weekday: w, open: false, slots: [] };
      });
    });
    return map;
  }, [rooms]);

  const [schedules, setSchedules] = useState(initialSchedules);
  const [selectedRoomId, setSelectedRoomId] = useState(rooms[0]?.id);
  const [closures, setClosures] = useState([
    { id: "c1", roomId: rooms[0]?.id, date: "2026-04-25", reason: "Festa della Liberazione" },
    { id: "c2", roomId: rooms[0]?.id, date: "2026-05-01", reason: "Festa del Lavoro" }
  ]);
  const [closureForm, setClosureForm] = useState({ date: "", reason: "" });

  const room = rooms.find(r => r.id === selectedRoomId);
  const kit = room ? getKitById(room.kitId) : null;

  const updateDay = (weekday, patch) => {
    setSchedules(prev => ({
      ...prev,
      [selectedRoomId]: prev[selectedRoomId].map(d => d.weekday === weekday ? { ...d, ...patch } : d)
    }));
  };

  const addSlot = (weekday) => {
    setSchedules(prev => ({
      ...prev,
      [selectedRoomId]: prev[selectedRoomId].map(d =>
        d.weekday === weekday ? { ...d, slots: [...d.slots, { start: "09:00", end: "13:00" }] } : d
      )
    }));
  };

  const removeSlot = (weekday, idx) => {
    setSchedules(prev => ({
      ...prev,
      [selectedRoomId]: prev[selectedRoomId].map(d =>
        d.weekday === weekday ? { ...d, slots: d.slots.filter((_, i) => i !== idx) } : d
      )
    }));
  };

  const updateSlot = (weekday, idx, field, value) => {
    setSchedules(prev => ({
      ...prev,
      [selectedRoomId]: prev[selectedRoomId].map(d =>
        d.weekday === weekday
          ? { ...d, slots: d.slots.map((s, i) => i === idx ? { ...s, [field]: value } : s) }
          : d
      )
    }));
  };

  const saveSchedule = () => {
    toast.success("Orari di apertura salvati", { description: `${room?.name} aggiornata correttamente.` });
  };

  const addClosure = () => {
    if (!closureForm.date || !closureForm.reason) {
      toast.error("Compila data e motivo");
      return;
    }
    setClosures(prev => [...prev, { id: `c${Date.now()}`, roomId: selectedRoomId, ...closureForm }]);
    setClosureForm({ date: "", reason: "" });
    toast.success("Chiusura aggiunta");
  };

  const removeClosure = (id) => {
    setClosures(prev => prev.filter(c => c.id !== id));
    toast.success("Chiusura rimossa");
  };

  const roomClosures = closures.filter(c => c.roomId === selectedRoomId);
  const orderedDays = [1, 2, 3, 4, 5, 6, 0];

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <CalendarRange className="h-4 w-4 text-indigo-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Pianificazione</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Apertura sale e chiusure</h1>
        <p className="text-sm text-muted-foreground mt-1">{pharmacy?.name} · Turni settimanali e festività</p>
      </div>

      {/* Room selector */}
      <div className="flex flex-wrap gap-2" data-testid="planning-room-selector">
        {rooms.map(r => (
          <Button
            key={r.id}
            variant={selectedRoomId === r.id ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedRoomId(r.id)}
            className="gap-2"
            data-testid={`planning-room-${r.id}`}
          >
            <DoorOpen className="h-4 w-4" />
            {r.name}
          </Button>
        ))}
      </div>

      <Tabs defaultValue="weekly" className="space-y-4">
        <TabsList>
          <TabsTrigger value="weekly" data-testid="planning-tab-weekly">Calendario settimanale</TabsTrigger>
          <TabsTrigger value="closures" data-testid="planning-tab-closures">Chiusure straordinarie</TabsTrigger>
        </TabsList>

        <TabsContent value="weekly" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-indigo-600" />
                    {room?.name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {kit?.name} · {room?.capabilities.length} prestazioni abilitate
                  </p>
                </div>
                <Button onClick={saveSchedule} variant="premium" size="sm" data-testid="planning-save-btn">
                  <Save className="h-4 w-4 mr-1" /> Salva orari
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {orderedDays.map(w => {
                const day = schedules[selectedRoomId]?.find(d => d.weekday === w);
                if (!day) return null;
                return (
                  <div key={w} className="rounded-lg border p-3 bg-card">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={day.open}
                          onCheckedChange={(v) => updateDay(w, { open: v, slots: v && day.slots.length === 0 ? [{ start: "09:00", end: "13:00" }] : day.slots })}
                          data-testid={`planning-day-toggle-${w}`}
                        />
                        <span className="font-semibold text-sm w-24">{weekdayLabels[w]}</span>
                        {!day.open && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Chiuso</Badge>}
                      </div>
                      {day.open && (
                        <Button variant="ghost" size="sm" onClick={() => addSlot(w)} className="gap-1">
                          <Plus className="h-3 w-3" /> Fascia
                        </Button>
                      )}
                    </div>
                    {day.open && (
                      <div className="space-y-2 ml-12">
                        {day.slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={slot.start}
                              onChange={(e) => updateSlot(w, idx, "start", e.target.value)}
                              className="w-32 h-8"
                            />
                            <span className="text-muted-foreground text-sm">→</span>
                            <Input
                              type="time"
                              value={slot.end}
                              onChange={(e) => updateSlot(w, idx, "end", e.target.value)}
                              className="w-32 h-8"
                            />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeSlot(w, idx)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        {day.slots.length === 0 && (
                          <p className="text-xs text-muted-foreground italic">Nessuna fascia oraria</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="closures" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertOctagon className="h-4 w-4 text-amber-600" /> Chiusure programmate · {room?.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 md:grid-cols-3 items-end">
                <div className="space-y-1">
                  <Label htmlFor="closure-date" className="text-xs">Data</Label>
                  <Input
                    id="closure-date"
                    type="date"
                    value={closureForm.date}
                    onChange={(e) => setClosureForm({ ...closureForm, date: e.target.value })}
                    data-testid="planning-closure-date"
                  />
                </div>
                <div className="space-y-1 md:col-span-2 md:flex md:gap-2 md:items-end">
                  <div className="flex-1">
                    <Label htmlFor="closure-reason" className="text-xs">Motivo</Label>
                    <Input
                      id="closure-reason"
                      placeholder="Es. Manutenzione, Festività..."
                      value={closureForm.reason}
                      onChange={(e) => setClosureForm({ ...closureForm, reason: e.target.value })}
                      data-testid="planning-closure-reason"
                    />
                  </div>
                  <Button onClick={addClosure} className="gap-1 mt-2 md:mt-0" data-testid="planning-closure-add">
                    <Plus className="h-4 w-4" /> Aggiungi
                  </Button>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                {roomClosures.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Nessuna chiusura programmata.</p>
                )}
                {roomClosures.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border bg-amber-50/50">
                    <div>
                      <p className="font-medium text-sm">{new Date(c.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                      <p className="text-xs text-muted-foreground">{c.reason}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeClosure(c.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerPlanningPage;
